import app from 'flarum/forum/app';
import MpSkeleton from './MpSkeleton';
import { api } from '../../common/utils/api';
import { takePreload } from '../../common/utils/preload';
import { cartStore } from '../../common/utils/cartStore';
import { formatMoney } from '../../common/utils/currency';
import { productUrl } from '../../common/utils/productUrl';
import { cartPulse } from '../../common/utils/cartPulse';
import { cartAddFeedback } from '../../common/utils/cartAddFeedback';
import MpSortDropdown from './MpSortDropdown';
import { mpImage } from '../../common/utils/mpImage';
/**
 * Discover — lista de extensões no estilo "marketplace de premiums".
 * Grid de cards modernos com ícone tintado, preço como botão outline,
 * stats no rodapé e "Ver detalhes" azul. Load more central.
 */
export default class DiscoverView {
  products = [];
  bundles = [];
  categories = [];
  loading = true;
  search = '';
  searchTimer = null;
  sort = 'newest';
  selectedCategory = null;
  tags = [];
  tagsLoading = false;
  selectedTag = null;
  hasMore = false;
  loadingMore = false;
  perPage = 24;
  offset = 0;
  addingBundleId = null;
  oninit() {
    // Preload do boot (PreloadPageData): grade, categorias, tags e bundles já
    // chegam no HTML da própria página. A loja pinta completa no 1º paint e
    // não gasta os 4 round-trips (cada um custa um boot inteiro do Flarum).
    // Sem preload — navegação SPA, ou falha no servidor — cai no load normal.
    const cats = takePreload('shop_categories');
    if (cats) this.categories = cats.data ?? [];else this.loadCategories();
    const tags = takePreload('shop_tags');
    if (tags) this.tags = tags.data ?? [];else this.loadTags();
    const bundles = takePreload('shop_bundles');
    if (bundles) this.bundles = bundles.data ?? [];else this.loadBundles();

    // O preload usa os MESMOS params do 1º fetch (sort=newest, offset=0,
    // limit=perPage), então `hasMore`/`offset` continuam consistentes com a
    // paginação de "carregar mais".
    const products = takePreload('shop_products');
    if (products) {
      const items = products.data ?? [];
      this.products = items;
      this.hasMore = items.length >= this.perPage;
      this.offset = items.length;
      this.loading = false;
    } else {
      this.fetch(true);
    }
    this.cartUnsub = cartStore.subscribe(() => m.redraw());
    cartStore.ensureFresh().catch(e => console.warn('[marketplace discover cart load]', e));
  }
  async loadBundles() {
    try {
      const r = await api('GET', '/marketplace/bundles');
      this.bundles = r.data ?? [];
      m.redraw();
    } catch {
      /* silent */
    }
  }
  async addBundle(bundle) {
    if (this.addingBundleId) return;
    this.addingBundleId = bundle.id;
    m.redraw();
    try {
      await cartStore.addBundle(bundle.id);
      cartAddFeedback.success(null);
      app.alerts.show({
        type: 'success'
      }, this.t('bundle_added') || `Bundle "${bundle.name}" adicionado ao carrinho`);
    } catch (e) {
      app.alerts.show({
        type: 'error'
      }, this.t('bundle_add_error') || 'Erro ao adicionar bundle');
    } finally {
      this.addingBundleId = null;
      m.redraw();
    }
  }
  onremove() {
    this.cartUnsub?.();
  }
  t(k, vars) {
    return app.translator.trans('ramon-marketplace.forum.discover.' + k, vars);
  }
  async loadCategories() {
    try {
      const r = await api('GET', '/marketplace/categories');
      this.categories = r.data ?? [];
      m.redraw();
    } catch {
      /* silent */
    }
  }
  async loadTags() {
    this.tagsLoading = true;
    try {
      const r = await api('GET', '/marketplace/tags');
      this.tags = r.data ?? [];
    } catch {
      /* silent */
    } finally {
      this.tagsLoading = false;
      m.redraw();
    }
  }
  toggleTag(slug) {
    this.selectedTag = this.selectedTag === slug ? null : slug;
    this.fetch(true);
  }
  renderTagFacet() {
    // Enquanto as tags não chegam, a fileira fica reservada com chips vazios:
    // sem isso ela nasce depois e empurra a grade inteira para baixo. Some se
    // a loja não tiver tag nenhuma (aí não há fileira no conteúdo real).
    if (!this.tags.length) {
      if (!this.tagsLoading) return null;
      return m("div", {
        className: "MpShop-tags MpSkel-mirror",
        "aria-hidden": "true"
      }, [0, 1, 2].map(i => m("div", {
        className: 'MpSkel MpSkel--chip MpSkel--' + (i === 1 ? 'chip-md' : 'chip-sm'),
        key: String(i)
      })));
    }
    return m("div", {
      className: "MpShop-tags"
    }, this.tags.map(t => m("button", {
      type: "button",
      key: t.slug,
      className: 'MpTagChip' + (this.selectedTag === t.slug ? ' is-active' : ''),
      onclick: () => this.toggleTag(t.slug)
    }, t.name, " ", m("span", {
      className: "MpTagChip-count"
    }, t.count))));
  }
  async fetch(reset) {
    if (reset === void 0) {
      reset = false;
    }
    if (reset) {
      this.offset = 0;
      this.loading = true;
      this.products = [];
    } else {
      this.loadingMore = true;
    }
    m.redraw();
    const params = new URLSearchParams();
    if (this.search) params.set('search', this.search);
    if (this.selectedCategory) params.set('category', String(this.selectedCategory));
    if (this.selectedTag) params.set('tag', this.selectedTag);
    // NÃO força type=digital — o backend já esconde physical se a setting estiver off,
    // então deixamos `digital` + `service` aparecerem juntos no Discover.
    params.set('sort', this.sort);
    params.set('limit', String(this.perPage));
    params.set('offset', String(this.offset));
    try {
      const r = await api('GET', '/marketplace/products?' + params.toString());
      const items = r.data ?? [];
      this.products = reset ? items : this.products.concat(items);
      this.hasMore = items.length >= this.perPage;
      this.offset += items.length;
    } catch {
      /* silent */
    } finally {
      this.loading = false;
      this.loadingMore = false;
      m.redraw();
    }
  }
  debouncedSearch() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.fetch(true), 300);
  }
  view() {
    return m("div", {
      className: "MpShop"
    }, this.renderHeader(), this.renderSection());
  }

  // Card de bundle no MESMO formato visual que MpShop-card (produto),
  // mantendo consistência da loja. Diferencia via chip "Pacote" + chip
  // "−X%" quando há economia. Card é um <a> que abre /bundle/:slug.
  renderBundleCard(b) {
    const savings = b.products_total - b.price;
    const pct = b.products_total > 0 ? Math.round(savings / b.products_total * 100) : 0;
    const color = this.colorFor(b.name);
    return m("a", {
      href: app.route('marketplace.bundle', {
        slug: b.slug
      }),
      config: m.route.link,
      className: "MpShop-card MpShop-card--bundle",
      key: b.id
    }, m("div", {
      className: "MpShop-cardHead"
    }, m("div", {
      className: "MpShop-cardIcon",
      style: {
        '--mp-icon-bg': color + '1f',
        '--mp-icon-fg': color
      }
    }, b.image ? m("img", Object.assign({}, mpImage(b.image), {
      alt: "",
      loading: "lazy"
    })) : m("i", {
      className: "fas fa-boxes"
    })), m("div", {
      className: "MpShop-cardMain"
    }, m("h3", {
      className: "MpShop-cardTitle"
    }, b.name), m("div", {
      className: "MpShop-cardChips"
    }, m("span", {
      className: "MpChip MpChip--ot"
    }, m("i", {
      className: "fas fa-boxes"
    }), " ", this.t('bundle_chip') || 'Pacote'), savings > 0 && m("span", {
      className: "MpChip MpChip--save"
    }, m("i", {
      className: "fas fa-tag"
    }), " \u2212", pct, "%"), m("span", {
      className: "MpChip"
    }, m("i", {
      className: "fas fa-cubes"
    }), " ", b.products.length), savings > 0 && m("span", {
      className: "MpChip MpChip--list",
      title: this.t('bundle_list_price') || 'Preço de lista'
    }, m("s", null, formatMoney(b.products_total))))), m("div", {
      className: "MpShop-priceBtn"
    }, formatMoney(b.price))), m("p", {
      className: "MpShop-cardDesc"
    }, b.description || this.t('bundle_default_desc') || 'Pacote com vários produtos juntos.'), m("div", {
      className: "MpShop-cardFoot"
    }, m("span", {
      className: "MpShop-cardCategory"
    }, b.products.slice(0, 3).map(p => p.name).join(' · '), b.products.length > 3 && ` +${b.products.length - 3}`), m("span", {
      className: "MpShop-cardDetails"
    }, this.t('details') || 'Ver detalhes', " ", m("i", {
      className: "fas fa-chevron-right"
    }))));
  }
  renderHeader() {
    return m("header", {
      className: "MpShop-header"
    }, m("h1", null, this.t('title')));
  }
  renderSection() {
    // Total de itens (soma de quantidades, não só linhas)
    const cart = cartStore.get();
    const totalInCart = cart.totals?.count ?? cart.items.reduce((sum, it) => sum + (it.quantity || 0), 0);
    return m("section", {
      className: "MpShop-section"
    }, m("div", {
      className: "MpShop-toolbar"
    }, m("div", {
      className: "MpShop-searchWrap"
    }, m("i", {
      className: "fas fa-search"
    }), m("input", {
      type: "text",
      placeholder: this.t('search_placeholder'),
      value: this.search,
      oninput: e => {
        this.search = e.target.value;
        this.debouncedSearch();
      }
    })), this.renderSortButton(), m("a", Object.assign({
      href: app.route('marketplace.cart'),
      config: m.route.link,
      className: "MpBtn MpBtn--primary MpShop-cartBtn"
    }, cartPulse()), m("i", {
      className: "fas fa-shopping-cart"
    }), " ", this.t('cart'), " (", totalInCart, ")")), this.renderTagFacet(), this.loading ? this.renderSkeleton() : this.products.length === 0 && this.bundles.length === 0 ? this.renderEmpty() : m('[', null, m("div", {
      className: "MpShop-grid"
    }, this.bundles.map(b => this.renderBundleCard(b)), this.products.map(p => this.renderCard(p))), this.hasMore && m("div", {
      className: "MpShop-loadMoreWrap"
    }, m("button", {
      type: "button",
      className: "MpBtn MpBtn--outline",
      disabled: this.loadingMore,
      onclick: () => this.fetch(false)
    }, this.loadingMore ? m("i", {
      className: "fas fa-circle-notch fa-spin"
    }) : m('[', null, m("i", {
      className: "fas fa-plus"
    }), " ", this.t('load_more') || 'Carregar mais')))));
  }
  renderSortButton() {
    // Mantemos o mapa de labels aqui (e não dentro do componente) porque ele
    // depende do translator do app + dos fallbacks específicos da loja.
    const sortOptions = [{
      key: 'newest',
      label: this.t('sort_newest') || 'Mais recentes'
    }, {
      key: 'trending',
      label: this.t('sort_trending') || 'Em alta'
    }, {
      key: 'price_asc',
      label: this.t('sort_price_asc') || 'Menor preço'
    }, {
      key: 'price_desc',
      label: this.t('sort_price_desc') || 'Maior preço'
    }, {
      key: 'name',
      label: this.t('sort_name') || 'Nome'
    }];
    return m(MpSortDropdown, {
      options: sortOptions,
      currentKey: this.sort,
      onChange: k => {
        this.sort = k;
        this.fetch(true);
      }
    });
  }
  renderCard(p) {
    const color = this.colorFor(p.name);
    const isRecurring = p.billing_cycle === 'monthly' || p.billing_cycle === 'yearly';
    const isFree = !!p.is_open_source;
    // Backend (ListProductsController) já devolve already_owned por produto
    // — uma flag booleana que cobre tanto subscriptions ativas quanto OrderItems
    // pagos do usuário logado. Físicos sempre re-compráveis (não considerados).
    // Pool unique_per_buyer também é re-comprável por design (cada compra
    // entrega um arquivo novo do pool), então NUNCA mostramos badge owned.
    const owned = !!p.already_owned && p.type !== 'physical' && !p.unique_per_buyer;
    const onSale = !!p.on_sale && p.sale_price != null;
    const priceLabel = isFree ? this.t('free') || 'Grátis' : formatMoney(onSale ? p.sale_price : p.price);
    return m("a", {
      href: productUrl(p),
      config: m.route.link,
      className: "MpShop-card",
      key: p.id
    }, m("div", {
      className: "MpShop-cardHead"
    }, m("div", {
      className: "MpShop-cardIcon",
      style: {
        '--mp-icon-bg': color + '1f',
        '--mp-icon-fg': color
      }
    }, p.image ? m("img", Object.assign({}, mpImage(p.image), {
      alt: "",
      loading: "lazy"
    })) : m("i", {
      className: this.iconFor(p)
    })), m("div", {
      className: "MpShop-cardMain"
    }, m("h3", {
      className: "MpShop-cardTitle"
    }, p.name), this.renderCardChips(p)), !isFree && p.price_yearly && Number(p.price_yearly) > 0 ? m("div", {
      className: "MpShop-priceBtn MpShop-priceBtn--dual"
    }, m("div", null, formatMoney(p.price), m("small", null, "/", this.t('per_month_short') || 'mês')), m("div", null, formatMoney(p.price_yearly), m("small", null, "/", this.t('per_year_short') || 'ano'))) : m("div", {
      className: 'MpShop-priceBtn' + (onSale ? ' MpShop-priceBtn--sale' : '')
    }, onSale && m("s", {
      className: "MpShop-priceWas"
    }, formatMoney(p.price)), priceLabel, isRecurring && !isFree && m("small", null, "/", p.billing_cycle === 'yearly' ? this.t('per_year_short') || 'ano' : this.t('per_month_short') || 'mês')), owned && m("span", {
      className: "MpShop-ownedBadge",
      "data-tooltip": app.translator.trans('ramon-marketplace.forum.product.already_owned'),
      "aria-label": app.translator.trans('ramon-marketplace.forum.product.already_owned')
    }, m("i", {
      className: "fas fa-check-circle"
    }))), p.description && p.description.trim() !== '' ? m("p", {
      className: "MpShop-cardDesc"
    }, p.description) : m("p", {
      className: "MpShop-cardDesc MpShop-cardDesc--muted"
    }, this.fallbackDesc(p)), m("div", {
      className: "MpShop-cardFoot"
    }, p.seller ? this.renderSellerTag(p.seller) : m("span", {
      className: "MpShop-cardCategory"
    }, p.category?.name || (p.package_name ? p.package_name : '')), m("span", {
      className: "MpShop-cardDetails"
    }, this.t('details') || 'Ver detalhes', " ", m("i", {
      className: "fas fa-chevron-right"
    }))));
  }
  renderSellerTag(seller) {
    return m("span", {
      className: "MpShop-cardSeller",
      role: "link",
      title: this.t('by_seller', {
        name: seller.store_name
      }) || seller.store_name,
      onclick: e => {
        e.preventDefault();
        e.stopPropagation();
        if (seller.username) m.route.set(app.route('marketplace.user_seller', {
          username: seller.username
        }));
      }
    }, m("i", {
      className: "fas fa-store"
    }), " ", seller.store_name, seller.rating > 0 && m("span", {
      className: "MpShop-cardSeller-rating"
    }, m("i", {
      className: "fas fa-star"
    }), " ", seller.rating.toFixed(1)));
  }
  renderCardChips(p) {
    // Ordem FIXA: billing (mensal/anual) → service → composer → oss → rating.
    // Garante que a mesma coluna do card sempre mostra a mesma info entre produtos.
    const chips = [];
    if (p.featured) {
      chips.push({
        icon: 'fas fa-star',
        label: this.t('staff_pick') || 'Staff pick',
        cls: 'MpChip--featured'
      });
    }

    // Se o produto oferece os dois ciclos (price_yearly preenchido), mostra
    // "Mensal × Anual" pra deixar claro que o cliente escolhe — em vez do
    // valor fixo do billing_cycle padrão.
    const hasDual = p.price_yearly != null && Number(p.price_yearly) > 0 && (p.billing_cycle === 'monthly' || p.billing_cycle === 'yearly');
    if (hasDual) {
      chips.push({
        icon: 'fas fa-sync-alt',
        label: this.t('monthly_or_yearly') || 'Mensal × Anual',
        cls: 'MpChip--sub'
      });
    } else if (p.billing_cycle === 'monthly') {
      chips.push({
        icon: 'fas fa-sync-alt',
        label: this.t('monthly') || 'Mensal',
        cls: 'MpChip--sub'
      });
    } else if (p.billing_cycle === 'yearly') {
      chips.push({
        icon: 'fas fa-sync-alt',
        label: this.t('yearly') || 'Anual',
        cls: 'MpChip--sub'
      });
    }
    if (p.is_book) chips.push({
      icon: 'fas fa-book',
      label: this.t('book_chip') || 'Livro',
      cls: 'MpChip--ot'
    });
    if (p.type === 'service') chips.push({
      icon: 'fas fa-concierge-bell',
      label: this.t('service') || 'Serviço',
      cls: 'MpChip--ot'
    });
    if (p.package_name) chips.push({
      icon: 'fab fa-php',
      label: 'Composer',
      cls: 'MpChip--ot'
    });
    if (p.is_open_source) chips.push({
      icon: 'fas fa-code',
      label: this.t('oss') || 'Open source',
      cls: 'MpChip--sub'
    });
    // Pool unique_per_buyer: mostra "X restantes" quando o pool está
    // limitado/baixo. Inspira urgência (efeito "últimas unidades") sem
    // poluir cards com pool grande.
    if (p.unique_per_buyer) {
      const left = Number(p.pool_available ?? 0);
      // Strings vivem em forum.shop.* (compartilhadas com ProductPage),
      // por isso path completo aqui — o t() da DiscoverView prefixa com
      // forum.discover.* e devolveria a key bruta.
      const label = left > 0 ? app.translator.trans('ramon-marketplace.forum.shop.pool_left', {
        count: left
      }) || `${left} restantes` : app.translator.trans('ramon-marketplace.forum.shop.pool_sold_out') || 'Esgotado';
      chips.push({
        icon: 'fas fa-ticket-alt',
        label,
        cls: 'MpChip--pool'
      });
    }
    if (p.rating > 0) chips.push({
      icon: 'fas fa-star',
      label: Number(p.rating).toFixed(1)
    });
    if (chips.length === 0) return null;
    return m("div", {
      className: "MpShop-cardChips"
    }, chips.slice(0, 3).map((c, i) => m("span", {
      key: i,
      className: 'MpChip ' + (c.cls || '')
    }, m("i", {
      className: c.icon
    }), " ", c.label)));
  }
  renderEmpty() {
    return m("div", {
      className: "MpShop-empty"
    }, m("i", {
      className: "fas fa-search"
    }), m("p", null, this.t('empty') || 'Nenhum produto encontrado.'));
  }
  renderSkeleton() {
    return m(MpSkeleton, {
      variant: "cards",
      count: this.perPage > 6 ? 6 : this.perPage
    });
  }

  /**
   * Descritor curto por tipo quando o produto não tem descrição — evita o card
   * com corpo vazio (mantém altura uniforme com conteúdo útil em vez de vão).
   */
  fallbackDesc(p) {
    if (p.is_book) return this.t('book_chip') || 'Livro digital';
    if (p.type === 'service') return this.t('service') || 'Serviço';
    if (p.package_name) return this.t('composer_desc') || 'Extensão / pacote Composer';
    if (p.billing_cycle === 'monthly' || p.billing_cycle === 'yearly') return this.t('subscription_desc') || 'Assinatura';
    return this.t('digital_desc') || 'Produto digital';
  }
  iconFor(p) {
    if (p.package_name) return 'fab fa-php';
    const slug = p.category?.slug ?? '';
    if (slug.includes('theme')) return 'fas fa-palette';
    if (slug.includes('language')) return 'fas fa-language';
    return 'fas fa-cloud-download-alt';
  }
  colorFor(name) {
    const palette = ['#3f88f6', '#8b5cf6', '#e7672e', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899'];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = h * 31 + name.charCodeAt(i) >>> 0;
    return palette[h % palette.length];
  }
}
flarum.reg.add('ramon-marketplace', 'forum/components/DiscoverView', DiscoverView);